/**
 * ACTIVITY-CORE (L2, POS variant) — CATALOG Trash: products, categories,
 * ingredients, employees… soft-deleted, with restore and permanent deletion.
 * Local-first: operates on the account's SQLite and syncs through the normal
 * write-path (the server records restored/deleted).
 *
 * The transactional domain (collected orders, payments, cash) has NO trash:
 * its corrections are explicit business operations recorded in history with
 * the actor and reason.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { createSignal, For, Show } from "solid-js";
import { ArchiveRestore, Trash2 } from "@fleet/icons";
import {
  getTrashItemsQuery,
  getTrashWatchSql,
  purgeTrashItemMutation,
  queryKeys,
  restoreTrashItemMutation,
  TRASH_PURGE_DAYS,
  type TrashEntityType,
  type TrashItem,
} from "@malla/shared";
import { createPowerSyncQuery } from "@malla/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@fleet/ui";
import { useActiveContext } from "@/features/pos/hooks/use-active-context";
import { formatRelativeTime } from "@/features/activity/lib/activity-text";

const TYPE_LABELS: Record<TrashEntityType, MessageDescriptor> = {
  menu_category: msg`Menu category`,
  menu_product: msg`Product`,
  product_variant: msg`Variant`,
  combo: msg`Combo`,
  ingredient: msg`Ingredient`,
  supplier: msg`Supplier`,
  employee: msg`Employee`,
  customer: msg`Customer`,
  promotion: msg`Promotion`,
};

const MS_PER_DAY = 86_400_000;

/**
 * "will be permanently deleted in N days" — countdown = deleted_at +
 * TRASH_PURGE_DAYS − now. Returns the remaining days (minimum 0) or null if the
 * job is disabled (`TRASH_PURGE_DAYS <= 0`) or the date is invalid.
 */
function daysUntilPurge(iso: string | null): number | null {
  if (TRASH_PURGE_DAYS <= 0 || !iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const purgeAt = date.getTime() + TRASH_PURGE_DAYS * MS_PER_DAY;
  return Math.max(0, Math.ceil((purgeAt - Date.now()) / MS_PER_DAY));
}

function TrashRow(props: { item: TrashItem; onRestore: () => void; onPurge: () => void }) {
  const { t, locale } = useI18n();
  const [confirmOpen, setConfirmOpen] = createSignal(false);
  const purgeIn = () => daysUntilPurge(props.item.deletedAt);

  return (
    <div
      class="flex items-center gap-3 rounded-xl border border-border/30 bg-card/60 px-4 py-3"
      data-testid="trash-item"
    >
      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-medium" data-testid="trash-item-name">
          {props.item.name}
        </p>
        <p class="text-xs text-muted-foreground">
          <span data-testid="trash-item-type">{t(TYPE_LABELS[props.item.entityType])}</span>
          {" · "}
          {t(msg`Deleted {when}`, {
            when: formatRelativeTime(props.item.deletedAt, locale()),
          })}
        </p>
        <Show when={purgeIn() !== null}>
          <p class="text-xs text-muted-foreground/80" data-testid="trash-item-purge-countdown">
            {t(msg`Will be permanently deleted in {days} days`, { days: purgeIn() ?? 0 })}
          </p>
        </Show>
      </div>
      <button
        type="button"
        class="shrink-0 rounded-md border border-border/40 px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
        onClick={() => props.onRestore()}
        data-testid="trash-item-restore"
      >
        <span class="inline-flex items-center gap-1.5">
          <ArchiveRestore class="h-3.5 w-3.5" />
          {t(msg`Restore`)}
        </span>
      </button>
      <button
        type="button"
        class="shrink-0 rounded-md border border-destructive/30 px-2.5 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
        onClick={() => setConfirmOpen(true)}
        data-testid="trash-item-purge"
        title={t(msg`Delete forever`)}
      >
        <Trash2 class="h-3.5 w-3.5" />
      </button>
      <ConfirmDialog
        open={confirmOpen()}
        onOpenChange={setConfirmOpen}
        title={t(msg`Delete forever?`)}
        description={t(msg`"{name}" will be permanently deleted. This cannot be undone.`, {
          name: props.item.name,
        })}
        variant="destructive"
        confirmLabel={t(msg`Delete forever`)}
        onConfirm={props.onPurge}
      />
    </div>
  );
}

function TrashSkeleton() {
  return (
    <div class="space-y-3" data-testid="trash-skeleton">
      <For each={[0, 1, 2]}>
        {() => (
          <div class="flex items-center gap-3 rounded-xl border border-border/20 px-4 py-3">
            <Skeleton class="h-9 w-9 animate-shimmer rounded-lg" />
            <div class="flex-1 space-y-1.5">
              <Skeleton class="h-3.5 w-1/2 animate-shimmer rounded" />
              <Skeleton class="h-3 w-1/4 animate-shimmer rounded" />
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

export function TrashView() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const ctx = useActiveContext();

  const trashQuery = createPowerSyncQuery<TrashItem>({
    queryKey: () => queryKeys.trash.items(userId(), ctx.org()?.id),
    db,
    queryFn: () => {
      const dbInstance = drizzle();
      const org = ctx.org();
      if (!dbInstance || !org) {
        return null;
      }
      // Client-side multi-table union; `toSQL` references all trash tables so
      // PowerSync re-executes when any one of them changes.
      return {
        execute: () => getTrashItemsQuery(dbInstance, org.id),
        toSQL: () => getTrashWatchSql(),
      };
    },
    enabled: () => Boolean(drizzle() && ctx.org()),
  });

  const handleRestore = async (item: TrashItem) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await restoreTrashItemMutation(dbInstance, item.entityType, item.id);
      showToast(t(msg`"{name}" restored`, { name: item.name }), "success");
    } catch {
      showToast(t(msg`Could not restore the item`), "error");
    }
  };

  const handlePurge = async (item: TrashItem) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await purgeTrashItemMutation(dbInstance, item.entityType, item.id);
      showToast(t(msg`Deleted forever`), "success");
    } catch {
      showToast(t(msg`Could not delete the item`), "error");
    }
  };

  return (
    <div class="space-y-6" data-testid="trash-page">
      <div class="flex items-center gap-3">
        <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Trash2 class="h-5 w-5" />
        </div>
        <div>
          <h1 class="text-2xl font-bold tracking-tight">{t(msg`Trash`)}</h1>
          <p class="text-sm text-muted-foreground">
            {t(msg`Deleted catalog items can be restored or removed forever.`)}
          </p>
        </div>
      </div>

      <Show
        when={!trashQuery.isFetching || (trashQuery.data ?? []).length > 0}
        fallback={<TrashSkeleton />}
      >
        <Show
          when={(trashQuery.data ?? []).length > 0}
          fallback={
            <EmptyState
              illustration="plate"
              title={t(msg`Trash is empty`)}
              description={t(msg`Deleted menu items, ingredients and staff will show up here.`)}
            />
          }
        >
          <div class="space-y-3" data-testid="trash-list">
            <For each={trashQuery.data ?? []}>
              {(item) => (
                <TrashRow
                  item={item}
                  onRestore={() => void handleRestore(item)}
                  onPurge={() => void handlePurge(item)}
                />
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
